import { useMemo, type FC } from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import CampaignFlowActions from "src/layouts/Engagements/Campaigns/shared/CampaignFlowActions";
import EmailStatusPopup from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/EmailStatusPopup";
import ChatScheduleStep from "./ChatScheduleStep";
import ChooseWhatsAppTemplateStep from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/steps/ChooseWhatsAppTemplateStep";
import WhatsAppEditorStep from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/steps/WhatsAppEditorStep";
import {
  WHATSAPP_CAMPAIGN_STEPS,
  WHATSAPP_EDITOR_KEY,
} from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignConstants";
import type { useChatWhatsAppCampaignWizard, ChatWhatsAppStepSnapshot } from "./useChatWhatsAppCampaignWizard";
import "src/layouts/Engagements/Campaigns/shared/CampaignFlow.scss";
import "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignWizard.scss";
import "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignEditor.scss";
import "./ChatCampaignWizard.scss";

const WIZARD_KEY = "campaignFlow.aiWizard";

export type { ChatWhatsAppStepSnapshot as FrozenWhatsAppSnapshot } from "./useChatWhatsAppCampaignWizard";

interface ChatWhatsAppCampaignWizardProps {
  wizard: ReturnType<typeof useChatWhatsAppCampaignWizard>;
  frozenSnapshot?: ChatWhatsAppStepSnapshot;
}

const ChatWhatsAppCampaignWizard: FC<ChatWhatsAppCampaignWizardProps> = ({
  wizard,
  frozenSnapshot,
}) => {
  const { t, i18n } = useTranslation();
  const frozen = Boolean(frozenSnapshot);
  const { templatesState, wizard: whatsAppWizard } = wizard;

  const stepIndex = frozenSnapshot?.stepIndex ?? whatsAppWizard.stepIndex;
  const stepKey = frozenSnapshot?.stepKey ?? whatsAppWizard.stepKey;
  const template = frozenSnapshot?.template ?? whatsAppWizard.template;
  const drafts = frozenSnapshot?.drafts ?? whatsAppWizard.drafts;
  const media = frozenSnapshot?.media ?? whatsAppWizard.media;

  const {
    isComplete,
    isSending,
    isScheduleOpen,
    deliveryStatus,
    audience,
    preview,
    updateDraft,
    attachMedia,
    removeMedia,
    goNext,
    goBack,
    selectTemplate,
    changeTemplate,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
  } = whatsAppWizard;

  const isEditorStep = stepKey === "editor";
  const stepCountLabel = useMemo(
    () =>
      t("wizardStepper.stepOf", {
        step: formatNumber(stepIndex + 1, {}, i18n.language),
        total: formatNumber(WHATSAPP_CAMPAIGN_STEPS.length, {}, i18n.language),
      }),
    [i18n.language, stepIndex, t]
  );

  const isNextDisabled = !template;
  const frozenClass = frozen ? " chat-campaign-wizard--frozen" : "";

  return (
    <div className={`campaign-flow chat-campaign-wizard${frozenClass}`}>
      <div className="chat-campaign-wizard__step-count">{stepCountLabel}</div>

      {isEditorStep && template ? (
        <WhatsAppEditorStep
          template={template}
          drafts={drafts}
          audience={audience}
          media={media}
          preview={preview}
          onDraftChange={updateDraft}
          onAttachMedia={attachMedia}
          onRemoveMedia={removeMedia}
          onChangeTemplate={changeTemplate}
        />
      ) : (
        <ChooseWhatsAppTemplateStep templatesState={templatesState} onUse={selectTemplate} />
      )}

      {!frozen && !isScheduleOpen && (
        <CampaignFlowActions onBack={goBack} wide>
          {isEditorStep ? (
            <>
              <button
                type="button"
                className="button button--primary campaign-flow__action"
                onClick={openSchedule}
                disabled={!isComplete}
              >
                {t(`${WIZARD_KEY}.createCampaign.scheduleCampaign`)}
              </button>
              <button
                type="button"
                className="button button--primary campaign-flow__action"
                onClick={sendNow}
                disabled={!isComplete}
              >
                {t(`${WIZARD_KEY}.createCampaign.sendCampaign`)}
              </button>
            </>
          ) : (
            <Tooltip
              title={isNextDisabled ? t(`${WHATSAPP_EDITOR_KEY}.chooseTemplate.nextDisabledHint`) : ""}
              placement="topRight"
              arrow={false}
              overlayClassName="wa-next-tooltip"
            >
              <span className="wa-next-tooltip__trigger">
                <button
                  type="button"
                  className="button button--primary campaign-flow__action"
                  onClick={goNext}
                  disabled={isNextDisabled}
                >
                  {t(`${WIZARD_KEY}.next`)}
                </button>
              </span>
            </Tooltip>
          )}
        </CampaignFlowActions>
      )}

      {!frozen && isScheduleOpen && (
        <ChatScheduleStep isSubmitting={isSending} onCancel={closeSchedule} onSchedule={schedule} />
      )}

      {!frozen && deliveryStatus && (
        <EmailStatusPopup
          type={deliveryStatus.type}
          date={deliveryStatus.date}
          time={deliveryStatus.time}
          onClose={clearDeliveryStatus}
        />
      )}
    </div>
  );
};

export default ChatWhatsAppCampaignWizard;
